import { Navigate, useNavigate } from '@/util/react-router-dom';
import { useEncounter } from '@/contexts/encounter-context';
import {
  EncounterStatusIdsType,
  IdentityVerificationStatus,
  IdentityVerificationType,
} from '@/api/api.schemas';
import { useGetEncountersIdentityVerificationInfo } from '@/api/get-verification-info';
import { MAX_IDV_RETRIES } from '@/util/constants';
import { useBranding } from '@/outlets/branding-outlet';
import { useVisitNavigation } from '@/contexts/visit-navigation-context';
import { VouchedComponent } from '@/components/vouched';
import {
  VouchedInstructionType,
  VouchedInstructions,
} from '@/components/vouched/vouched-instructions';
import { useGetPatient } from '@/api/get-personal-info';
import { useLoggedUser } from '@/contexts/current-user-context';
import { formatDate } from '@/util/formats';
import { VisitTitleButtons } from '@/components/navigation/visit-title-buttons';
import styles from './vouched-page.module.css';
import { useMediaQuery } from '@/hooks/use-media-query';
import { SaveProgressButton } from '@/components/navigation/save-progress-button';
import { Loader } from '@/components/loaders/loader';
import { useCallback, useState } from 'react';
import { useCreateEncountersIdentityVerification } from '@/api/create-identity-verification';
import { VouchedJob } from 'vouched';

const IDENTITY_VALIDATED_STATE = { isValidated: true };

export function VouchedPage() {
  const { encounter, invalidateEncounter } = useEncounter();
  const { hasIdVerification } = useBranding();
  const { data: verificationInfo, isFetching: isFetchingVerificationInfo } =
    useGetEncountersIdentityVerificationInfo(encounter.id);
  const { hasCompletedStep } = useVisitNavigation();
  const { user } = useLoggedUser();
  const { data: patient, isFetching: isFetchingPatient } = useGetPatient(user.id);
  const isMobile = useMediaQuery('--screen-md');
  const [hasStartedVerification, setHasStartedVerification] = useState(false);
  const navigate = useNavigate();
  const { mutate: createIdentityVerification } = useCreateEncountersIdentityVerification();

  const surrogate = patient?.surrogate;
  const hasSurrogate = !!surrogate;

  const handleOnDone = useCallback(
    (job: VouchedJob) => {
      if (!verificationInfo) return;

      createIdentityVerification(
        {
          encounterId: encounter.id,
          data: {
            jobId: job.id,
            isVerified: job.result.success,
            type: verificationInfo.type,
          },
        },
        {
          onSuccess: () => {
            if (job.result?.success) {
              if (hasSurrogate && verificationInfo.type === IdentityVerificationType.Idv) {
                navigate(`/visit/${encounter.id}/identity-verification/dependent`, {
                  replace: true,
                  state: IDENTITY_VALIDATED_STATE,
                });
              } else {
                invalidateEncounter();
                navigate(`/visit/${encounter.id}/payment`, {
                  replace: true,
                  state: IDENTITY_VALIDATED_STATE,
                });
              }
            } else {
              navigate(`/visit/${encounter.id}/identity-verification/rejection`);
            }
          },
          onError: () => {
            navigate(`/system-error`);
          },
        },
      );
    },
    [
      createIdentityVerification,
      encounter.id,
      verificationInfo,
      hasSurrogate,
      navigate,
      invalidateEncounter,
    ],
  );

  if (!hasIdVerification) {
    return <Navigate to={`/visit/${encounter.id}/review`} replace />;
  }

  if (hasCompletedStep(EncounterStatusIdsType.PreSubmitIdentityVerification)) {
    return <Navigate to={`/visit/${encounter.id}/payment`} replace />;
  }

  if (isFetchingPatient || isFetchingVerificationInfo) {
    return <Loader type="large" />;
  }

  if (!verificationInfo) {
    return <Navigate to={`/system-error`} />;
  }

  if (verificationInfo.manualReviewStatus === IdentityVerificationStatus.ManualReview) {
    return <Navigate to="/" />;
  }

  if (
    verificationInfo.manualReviewStatus === IdentityVerificationStatus.ManuallyApproved ||
    verificationInfo.manualReviewStatus === IdentityVerificationStatus.Approved
  ) {
    return (
      <Navigate to={`/visit/${encounter.id}/payment`} replace state={IDENTITY_VALIDATED_STATE} />
    );
  }

  if (verificationInfo.currentRetries >= MAX_IDV_RETRIES) {
    return <Navigate to={`/visit/${encounter.id}/identity-verification/rejection`} replace />;
  }

  const personInfo =
    verificationInfo.type === IdentityVerificationType.Idv && hasSurrogate
      ? {
          firstName: surrogate.firstName,
          lastName: surrogate.lastName,
          birthDate: formatDate(surrogate.birthday),
        }
      : {
          firstName: patient?.firstName,
          lastName: patient?.lastName,
          birthDate: formatDate(patient?.dateOfBirth),
        };

  const instructionType = getInstructionType(hasSurrogate, verificationInfo.type);
  const showInstructions = instructionType !== undefined && !hasStartedVerification;

  return (
    <div className={styles.instructionsPage}>
      <div className={styles.container}>
        <VisitTitleButtons
          className={styles.titleButtons}
          saveProgress={{ showModal: true }}
          logo={<img src={'/vouched-logo.svg'} alt="logo" />}
        />
        {isMobile && <img src={'/vouched-logo.svg'} alt="logo" className={styles.logo} />}
        {isMobile && <SaveProgressButton showModal />}
      </div>
      {showInstructions ? (
        <VouchedInstructions
          type={instructionType}
          onContinue={() => setHasStartedVerification(true)}
        />
      ) : (
        <VouchedComponent
          hasSurrogate={hasSurrogate}
          personInfo={personInfo}
          jobInfo={{
            initialJobID: verificationInfo.jobId ?? undefined,
            type: verificationInfo.type,
          }}
          onDone={handleOnDone}
        />
      )}
    </div>
  );
}

function getInstructionType(
  hasSurrogate: boolean,
  verificationType: IdentityVerificationType,
): VouchedInstructionType | undefined {
  if (!hasSurrogate) {
    return undefined;
  }

  if (verificationType === IdentityVerificationType.Reverify) {
    return 'reverify';
  }

  if (verificationType === IdentityVerificationType.SelfieVerification) {
    return 'patient';
  }

  if (verificationType === IdentityVerificationType.Idv) {
    return 'surrogate';
  }

  return undefined;
}
